import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { LoadProductContext } from '@/modules/supply/components/SupplyLoadVisualization';
import { useLoadRestriction, type FillQuantityChange } from '@/modules/supply/hooks/useLoadRestriction';
import type { LoadEstimate } from '@/modules/supply/hooks/useLoadEstimate';
import { coverageDaysFromHistoricalSales } from '@/modules/supply/utils/coverageDays';
import { combineLoadEstimate, withLineQuantities, type LoadEstimateSupplierResult } from '@/modules/supply/utils/loadEstimate';
import { LOAD_ESTIMATE_KEY as T, containerCodeOf, num } from '@/modules/supply/utils/loadEstimateDraft';
import { buildOccupancyBreakdown } from '@/modules/supply/utils/loadEstimateOccupancy';
import { loadEstimateService } from '@/services/loadEstimateService';

/** Lo que se ve del camión: proveedor activo, consolidación de los marcados y restricción logística. */
export function useLoadEstimateTruck(estimate: LoadEstimate) {
  const { t, i18n } = useTranslation();
  const {
    warehouseId, enabled, sources, setSources, marked, activeSupplierId, templates, abcByProduct, draftRef, calculateSuppliers,
  } = estimate;
  // Consolidación: varios proveedores marcados en un solo contenedor (por defecto, un contenedor por proveedor).
  const [consolidate, setConsolidate] = useState(false);
  const [consolidateTemplateId, setConsolidateTemplateId] = useState<number | null>(null);
  const [colorBy, setColorBy] = useState<'abc' | 'supplier'>('abc');
  // Productos que la restricción logística no debe tocar.
  const [lockedProducts, setLockedProducts] = useState<Record<number, boolean>>({});

  const activeSource = useMemo(
    () => sources.find((source) => source.supplierId === activeSupplierId && !source.unavailableReason)
      ?? sources.find((source) => !source.unavailableReason)
      ?? null,
    [sources, activeSupplierId],
  );
  const breakdowns = useMemo(() => {
    const result = new Map<number, ReturnType<typeof buildOccupancyBreakdown>>();
    sources.forEach((source) => {
      if (!source.unavailableReason) result.set(source.supplierId, buildOccupancyBreakdown(source.result, abcByProduct, t(`${T}.occupancyTotal`)));
    });
    return result;
  }, [sources, abcByProduct, t]);
  const activeBreakdown = activeSource ? breakdowns.get(activeSource.supplierId) ?? null : null;

  // Columnas de contexto de la tabla de productos (stock, ventas, tránsito, cobertura y ABC): las mismas cifras que la bandeja.
  const activeProductIds = useMemo(
    () => (activeSource?.result.before?.lines_detail ?? []).map((line) => num(line.product_id)).filter((id) => id > 0),
    [activeSource],
  );
  const contextQuery = useQuery({
    queryKey: ['loadEstimate', 'productContext', warehouseId, activeSource?.supplierId, activeProductIds.join(',')],
    queryFn: () => loadEstimateService.productContext(warehouseId as number, activeProductIds),
    enabled: enabled && activeProductIds.length > 0,
    staleTime: 60_000,
  });
  const productContext = useMemo<Record<number, LoadProductContext>>(() => {
    const data = contextQuery.data;
    if (!data || !activeSource) return {};
    const quantityByProduct = new Map((activeSource.result.before?.lines_detail ?? []).map((line) => [
      num(line.product_id), num(line.suggested_units ?? line.suggested_quantity),
    ]));
    return Object.fromEntries(Object.entries(data).map(([productId, row]) => [
      Number(productId),
      {
        stock: row.stock,
        inTransit: row.in_transit,
        monthlySales: row.monthly_sales,
        estimatedMonthlySales: row.estimated_monthly_sales,
        coverageDays: row.coverage_days,
        // Cobertura si llegara lo que se va a pedir (la cantidad vigente de la línea, con sus ajustes).
        coverageWithSuggestedDays: coverageDaysFromHistoricalSales((row.stock ?? 0) + (quantityByProduct.get(Number(productId)) ?? 0), row.monthly_sales),
        abcCategory: abcByProduct[productId] ?? null,
      },
    ]));
  }, [contextQuery.data, activeSource, abcByProduct]);

  // Productos del proveedor cuya cantidad ya no es la validada (p. ej. redondeados a cajas completas).
  const adjustedItems = (() => {
    if (!activeSource) return [];
    const original = new Map((draftRef.current?.lines ?? []).map((line) => [line.product_id, num(line.suggested_quantity)]));
    return (activeSource.result.before?.lines_detail ?? []).flatMap((line) => {
      const productId = num(line.product_id);
      const quantity = num(line.suggested_units ?? line.suggested_quantity);
      return original.has(productId) && original.get(productId) !== quantity ? [{ key: `${activeSource.supplierId}:${productId}`, original: original.get(productId) as number }] : [];
    });
  })();
  // El camión se llena con el porcentaje total de ocupación del proveedor.
  const fillPct = activeBreakdown?.total.pct ?? null;

  // Verificación de consolidación: la carga de los proveedores marcados sumada frente a un solo contenedor.
  const estimableMarked = useMemo(
    () => sources.filter((source) => marked.includes(source.supplierId) && !source.unavailableReason),
    [sources, marked],
  );
  const consolidatedView = useMemo(() => {
    if (!consolidate || estimableMarked.length < 2) return null;
    const base = combineLoadEstimate(estimableMarked, estimableMarked[0].supplierId);
    if (!base) return null;
    const template = templates.find((item) => item.id === consolidateTemplateId)
      ?? templates.find((item) => item.code === containerCodeOf(estimableMarked[0]));
    const container = base.container || {};
    const usableVolume = template?.usable_volume_m3 ?? num(container.usable_volume_m3);
    const usableWeight = template?.usable_payload_kg ?? num(container.usable_payload_kg);
    // Si algún proveedor no tiene volumen o peso, el total es desconocido: no se puede afirmar que cabe.
    const known = base.before?.volume_m3 != null && base.before?.weight_kg != null;
    const volume = num(base.before?.volume_m3);
    const weight = num(base.before?.weight_kg);
    const volumePct = known && usableVolume > 0 ? (volume / usableVolume) * 100 : null;
    const weightPct = known && usableWeight > 0 ? (weight / usableWeight) * 100 : null;
    return {
      template,
      volumePct,
      weightPct,
      known,
      fits: known && (volumePct ?? 0) <= 100 && (weightPct ?? 0) <= 100,
      result: { ...base, container: { ...container, container_code: template?.code ?? container.container_code, description: template?.name ?? container.description, usable_volume_m3: usableVolume, usable_payload_kg: usableWeight } },
    };
  }, [consolidate, estimableMarked, templates, consolidateTemplateId]);

  // «Aplicar restricción logística»: actúa sobre el vehículo que se está viendo (un proveedor o la consolidación).
  const truckSources = useMemo(
    () => (consolidatedView ? estimableMarked : activeSource ? [activeSource] : []),
    [consolidatedView, estimableMarked, activeSource],
  );
  const truckTemplateId = consolidatedView
    ? consolidatedView.template?.id ?? null
    : activeSource ? templates.find((item) => item.code === containerCodeOf(activeSource))?.id ?? null : null;
  const lockedProductIds = useMemo(
    () => Object.keys(lockedProducts).filter((id) => lockedProducts[Number(id)]).map(Number),
    [lockedProducts],
  );
  const linesOf = (source: LoadEstimateSupplierResult) => Object.fromEntries((source.result.before?.lines_detail ?? []).map((line) => [
    String(line.product_id), num(line.suggested_units ?? line.suggested_quantity),
  ]));
  const buildTruckQuantities = useCallback(
    () => Object.assign({}, ...truckSources.map((source) => linesOf(source))) as Record<string, number>,
    [truckSources],
  );
  const applyTruckQuantities = useCallback(async (changes: FillQuantityChange[]) => {
    const supplierIds = [...new Set(changes.map((change) => change.supplierId))];
    const overrides: Record<number, Record<string, number>> = {};
    const containers: Record<number, number> = {};
    supplierIds.forEach((supplierId) => {
      const source = sources.find((item) => item.supplierId === supplierId);
      if (!source) return;
      const quantities = linesOf(source);
      changes.filter((change) => change.supplierId === supplierId).forEach((change) => {
        if (change.units > 0) quantities[String(change.productId)] = change.units;
        else delete quantities[String(change.productId)];
      });
      overrides[supplierId] = quantities;
      const template = templates.find((item) => item.code === containerCodeOf(source));
      if (template) containers[supplierId] = template.id;
    });
    const updated = await calculateSuppliers(supplierIds, overrides, containers);
    const failed = updated.find((item) => item.unavailableReason);
    if (failed) throw new Error(`${failed.supplierName}: ${failed.unavailableReason}`);
    setSources((current) => current.map((item) => updated.find((candidate) => candidate.supplierId === item.supplierId) ?? item));
  }, [sources, setSources, templates, calculateSuppliers]);
  const restriction = useLoadRestriction({
    warehouseId: typeof warehouseId === 'number' ? warehouseId : null,
    locale: i18n.language,
    t,
    supplierIds: truckSources.map((source) => source.supplierId),
    referenceSupplierId: truckSources[0]?.supplierId ?? null,
    containerTemplateId: truckTemplateId,
    buildQuantities: buildTruckQuantities,
    lockedProductIds,
    applyQuantities: applyTruckQuantities,
    resetKey: `${truckSources.map((source) => source.supplierId).join(',')}:${truckTemplateId ?? ''}`,
  });

  const changeQuantities = (quantities: Record<string, number>) => {
    if (Object.keys(quantities).length === 0) return;
    setSources((current) => withLineQuantities(current, quantities));
  };
  const roundedKey = (item: { productId: number; supplierId: number | null }) => `${item.supplierId}:${item.productId}`;
  const restoreValidated = () => changeQuantities(Object.fromEntries(adjustedItems.map((item) => [item.key, item.original])));

  return {
    consolidate,
    setConsolidate,
    setConsolidateTemplateId,
    colorBy,
    setColorBy,
    lockedProducts,
    setLockedProducts,
    lockedProductIds,
    activeSource,
    activeBreakdown,
    productContext,
    adjustedItems,
    fillPct,
    estimableMarked,
    consolidatedView,
    restriction,
    changeQuantities,
    roundedKey,
    restoreValidated,
  };
}

export type LoadEstimateTruck = ReturnType<typeof useLoadEstimateTruck>;
